import { createContext, useState, ReactNode, useEffect } from "react";

export type PortalContext = {
  type: "org" | "merchant";
  id: string;
};

const STORAGE_KEY = "portalContext";
// Pre-organizations key, still read so an existing session keeps its comercio,
// and still written so rolling this change back does not reset anyone.
const LEGACY_MERCHANT_ID_KEY = "merchantId";

function readStoredContext(): PortalContext | undefined {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as Partial<PortalContext>;
      if (
        (parsed?.type === "org" || parsed?.type === "merchant") &&
        typeof parsed.id === "string" &&
        parsed.id
      )
        return { type: parsed.type, id: parsed.id };
    } catch {
      // Unparseable: fall through to the legacy key.
    }
  }

  const legacyMerchantId = localStorage.getItem(LEGACY_MERCHANT_ID_KEY);
  if (legacyMerchantId) return { type: "merchant", id: legacyMerchantId };

  return undefined;
}

interface MerchantIdContextType {
  context: PortalContext | undefined;
  setContext: (context: PortalContext) => void;
}

export const MerchantContext = createContext<MerchantIdContextType | undefined>(
  undefined
);

export function MerchantProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<PortalContext | undefined>(
    readStoredContext
  );

  useEffect(() => {
    if (!context) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
    if (context.type === "merchant")
      localStorage.setItem(LEGACY_MERCHANT_ID_KEY, context.id);
  }, [context]);

  return (
    <MerchantContext.Provider
      value={{
        context,
        setContext,
      }}
    >
      {children}
    </MerchantContext.Provider>
  );
}
